import type { Demeanour, ObservedView, PersonCard, ProfileView, TextPart } from "./types";

/**
 * Presentation helpers for people: how they seem to you, in words (never numbers, 17 §14),
 * linked names in authored text, and the per-run "intro seen" marker.
 */

/** How someone seems towards you, as a short phrase. */
export const TOWARD_YOU_PHRASES: Record<Demeanour["towardYou"], string> = {
  warm: "warm towards you",
  friendly: "friendly towards you",
  professional: "professional",
  polite: "polite",
  guarded: "guarded with you",
  cool: "cool towards you",
};

export function towardYouPhrase(towardYou: Demeanour["towardYou"]): string {
  return TOWARD_YOU_PHRASES[towardYou] ?? "hard to read";
}

/** How someone seems in themselves, if anything shows; null when nothing does. */
export function seemsPhrase(seems: Demeanour["seems"]): string | null {
  return seems ? `Seems ${seems}` : null;
}

export function capitalise(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** "Friendly towards you. Seems stretched." */
export function demeanourSentence(d: Demeanour): string {
  const seems = seemsPhrase(d.seems);
  return `${capitalise(towardYouPhrase(d.towardYou))}.${seems ? ` ${seems}.` : ""}`;
}

/** Availability worth mentioning; nothing when they are in. */
export function availabilityWord(a: PersonCard["availability"]): string | null {
  return a === "away" ? "Away from work" : a === "leaving" ? "Has resigned" : null;
}

const stripQuotes = (s: string) => s.trim().replace(/^["“]|["”]$/g, "");

/** "Last: week 2, Holiday cover: you said “Yes, take it.”" */
export function lastDealingsLine(last: ProfileView["lastDealings"]): string | null {
  if (!last) return null;
  return `Last: week ${last.week}, ${stripQuotes(last.title)}: you said “${stripQuotes(last.reply)}”`;
}

/** Text parts to render, falling back to the plain text when the server sent none. */
export function partsOrText(parts: TextPart[] | undefined | null, text: string): TextPart[] {
  return parts && parts.length > 0 ? parts : [{ text }];
}

export type Segment = { kind: "text"; text: string } | { kind: "person"; text: string; personId: string };

/**
 * Turn parts into what is drawn: a part becomes a linked name only when it names someone you have
 * a profile for (not you, not someone who has left) and has visible text. Adjacent plain text is merged.
 */
export function segments(parts: TextPart[], known: (personId: string) => boolean): Segment[] {
  const out: Segment[] = [];
  for (const p of parts) {
    if (p.personId && p.text.trim() !== "" && known(p.personId)) {
      out.push({ kind: "person", text: p.text, personId: p.personId });
      continue;
    }
    const prev = out[out.length - 1];
    if (prev && prev.kind === "text") prev.text += p.text;
    else if (p.text !== "") out.push({ kind: "text", text: p.text });
  }
  return out;
}

/** The text a screen reader hears as the description of a linked name. */
export function personSummary(person: PersonCard, profile: ProfileView): string {
  const bits = [`${person.name}, ${person.role}.`];
  const away = availabilityWord(person.availability);
  if (away) bits.push(`${away}.`);
  if (profile.bio) bits.push(profile.bio);
  bits.push(`How they seem with you: ${demeanourSentence(profile.demeanour)}`);
  const last = lastDealingsLine(profile.lastDealings);
  if (last) bits.push(last);
  return bits.join(" ");
}

// --- intro -------------------------------------------------------------------------------------

export const introSeenKey = (runId: string) => `lawsim.intro-seen.${runId}`;

export function hasSeenIntro(runId: string): boolean {
  try {
    return localStorage.getItem(introSeenKey(runId)) === "1";
  } catch {
    return false;
  }
}

export function markIntroSeen(runId: string): void {
  try {
    localStorage.setItem(introSeenKey(runId), "1");
  } catch {
    // storage unavailable: the intro may show again after a reload, which is harmless
  }
}

/** The intro opens a run: tick 0, nothing answered yet, an intro to show, and not already seen. */
export function shouldShowIntro(view: Pick<ObservedView, "intro" | "clock" | "inbox" | "run">, seen: boolean): boolean {
  // In quick play, routine items are handled (and land in the inbox) before the player has done anything.
  const untouched = view.run.mode === "quick" || view.inbox.length === 0;
  return !seen && view.intro !== null && view.intro !== undefined && view.clock.tick === 0 && untouched && !view.run.ended;
}

// --- hover card placement ----------------------------------------------------------------------

export interface Rect { top: number; bottom: number; left: number; right: number }
export interface Size { width: number; height: number }

/**
 * Where to put a hover card: below the name if it fits, otherwise above if there is more room
 * there; always kept inside the viewport horizontally.
 */
export function placeCard(anchor: Rect, card: Size, viewport: Size, gap = 6, margin = 8): { top: number; left: number; side: "below" | "above" } {
  const below = viewport.height - anchor.bottom - gap - margin;
  const above = anchor.top - gap - margin;
  const side = card.height <= below || below >= above ? "below" : "above";
  const rawTop = side === "below" ? anchor.bottom + gap : anchor.top - gap - card.height;
  const top = Math.max(margin, Math.min(rawTop, viewport.height - margin - card.height));
  const maxLeft = Math.max(margin, viewport.width - margin - card.width);
  const left = Math.min(Math.max(anchor.left, margin), maxLeft);
  return { top, left, side };
}
